// =============================================================================
// MoreMenu — a "More" button that opens a short menu of LABELLED actions, for a
// page header with more buttons than a phone row holds (packhouse audit STK-01,
// PW-13). Stock carried eight icon-only buttons whose only names were `title`
// tooltips — which never show on touch — so a storeman could not tell Counts
// from Categories from Scan. Every item here carries its word.
//
// Menu semantics: the button reports aria-haspopup/aria-expanded, the list is
// role="menu" with role="menuitem" rows, focus moves into it on open, ↑/↓ move
// through it, and Escape or a tap outside closes it (the NotificationBell /
// AssignChecklistButton idiom). Rows are 44px.
// =============================================================================
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { MoreHorizontal, type LucideIcon } from 'lucide-react'

export interface MoreMenuItem {
  label:     string
  icon?:     LucideIcon
  onSelect:  () => void
  /** Greyed and inert — e.g. while this item's own action is still running. */
  disabled?: boolean
  /** A second line saying WHEN to choose this — for a menu whose items are
   *  alternatives the reader must pick between (Edit / Credit note / Void on
   *  an invoice). Kept out of the item's accessible name: read as its
   *  description, so "Void" is still found and announced as "Void". */
  hint?: string
}

/** Which edge the panel hangs from. Right-anchored (grows leftwards) is the
 *  default; when the trigger sits near the LEFT edge (the button wrapped onto
 *  its own row on a phone) that clips the panel, so flip to left-anchored. */
export function pickMenuAlign(triggerLeft: number, triggerRight: number, panelWidth: number, viewportWidth: number, gutter = 8): 'right' | 'left' {
  if (triggerRight - panelWidth >= gutter) return 'right'
  if (triggerLeft + panelWidth <= viewportWidth - gutter) return 'left'
  return triggerLeft > viewportWidth - triggerRight ? 'right' : 'left'
}

export default function MoreMenu({ items, label = 'More', ariaLabel }: {
  items:  MoreMenuItem[]
  /** The button's word. */
  label?: string
  /** A longer accessible name for the button when several menus share a page ("More for Sipho"). Keep the visible word in it. */
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const [align, setAlign] = useState<'right' | 'left'>('right')
  const panelRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !ref.current || !panelRef.current) return
    const t = ref.current.getBoundingClientRect()
    const w = panelRef.current.getBoundingClientRect().width
    if (w > 0) setAlign(pickMenuAlign(t.left, t.right, w, window.innerWidth))
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus()
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (items.length === 0) return null

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const rows = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [])
    const at = rows.indexOf(document.activeElement as HTMLElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % rows.length : (at - 1 + rows.length) % rows.length
    rows[next]?.focus()
  }

  return (
    <div ref={ref} data-theme="dark" className="relative">
      <button type="button" aria-label={ariaLabel} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(o => !o)}
        className="min-h-[44px] px-2.5 rounded-xl surface hover:bg-raised text-sm text-gray-200 inline-flex items-center gap-1.5 pressable">
        <MoreHorizontal size={16} aria-hidden="true" /> {label}
      </button>
      {open && (
        <div ref={panelRef} id={menuId} role="menu" aria-label={label} tabIndex={-1} onKeyDown={onMenuKey} data-theme="dark"
          className={`absolute ${align === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'} mt-1 z-dropdown motion-safe:animate-menu-in min-w-[13rem] max-w-[calc(100vw-1rem)] surface-sunk text-fg border hairline-strong rounded-xl shadow-xl py-1`}>
          {items.map(({ label: text, icon: Icon, onSelect, disabled, hint }, i) => (
            <button key={text} type="button" role="menuitem" disabled={disabled} aria-disabled={disabled || undefined}
              aria-label={hint ? text : undefined} aria-describedby={hint ? `${menuId}-hint-${i}` : undefined}
              onClick={() => { setOpen(false); onSelect() }}
              className="w-full min-h-[44px] px-3 text-left text-sm hover:bg-surface active:bg-surface focus:bg-surface outline-none inline-flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent">
              {Icon && <Icon size={16} className="text-gray-400 shrink-0" />}
              {hint ? (
                <span className="flex flex-col py-1.5 min-w-0">
                  <span>{text}</span>
                  <span id={`${menuId}-hint-${i}`} className="text-[11px] text-muted leading-snug">{hint}</span>
                </span>
              ) : text}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
